import React, { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Shield, ShieldCheck, Check, X, Play, Loader2, Users, KeyRound, Lock, Mail, Search, Globe } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Navbar } from "@/components/Navbar";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { AdminLogin } from "@/components/AdminLogin";
import { toast } from "sonner";

export default function Admin() {
  const { user, loading } = useAuth();
  const [keywords, setKeywords] = useState([]);
  const [apps, setApps] = useState([]);
  const [users, setUsers] = useState([]);
  const [messages, setMessages] = useState([]);
  const [running, setRunning] = useState(false);
  const [userSearch, setUserSearch] = useState("");
  const [kwRegionFilter, setKwRegionFilter] = useState("all");
  const [kwSearch, setKwSearch] = useState("");
  const [kwSearchDebounced, setKwSearchDebounced] = useState("");

  const load = useCallback(async () => {
    try {
      const [k, a, u] = await Promise.all([
        api.get("/admin/keywords"),
        api.get("/admin/brand-lock"),
        api.get("/admin/users"),
      ]);
      setKeywords(k.data.keywords);
      setApps(a.data.applications);
      setUsers(u.data.users);
      try { const c = await api.get("/admin/contact"); setMessages(c.data.messages); } catch {}
    } catch {}
  }, []);

  const loadUsers = useCallback(async (q) => {
    try {
      const u = await api.get("/admin/users", { params: q ? { search: q } : {} });
      setUsers(u.data.users);
    } catch {}
  }, []);

  const isAdmin = !!user && user.role === "admin";

  useEffect(() => { document.title = "Admin — PodiumOne"; }, []);

  useEffect(() => {
    if (loading || !isAdmin) return;
    load();
  }, [isAdmin, loading, load]);

  // Debounced admin user search (by name / email / brand name).
  useEffect(() => {
    if (loading || !isAdmin) return;
    const t = setTimeout(() => loadUsers(userSearch.trim()), 300);
    return () => clearTimeout(t);
  }, [userSearch, isAdmin, loading, loadUsers]);

  // Debounced admin keyword search (client-side, by slug / business name).
  useEffect(() => {
    const t = setTimeout(() => setKwSearchDebounced(kwSearch.trim().toLowerCase()), 300);
    return () => clearTimeout(t);
  }, [kwSearch]);

  const filteredKeywords = keywords.filter((k) => {
    const region = k.region || "global";
    if (kwRegionFilter !== "all" && region !== kwRegionFilter) return false;
    if (!kwSearchDebounced) return true;
    const haystack = [k.slug, k.name, ...(k.business_names || [])]
      .filter(Boolean).join(" ").toLowerCase();
    return haystack.includes(kwSearchDebounced);
  });

  const verify = async (slug, on, region) => {
    try {
      await api.post(`/admin/keywords/${slug}/${on ? "verify" : "unverify"}`, null, { params: { region } });
      toast.success(on ? "Keyword verified" : "Keyword unverified");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const toggleIndex = async (slug, on, region) => {
    try {
      await api.patch(`/admin/keywords/${slug}/indexing`, { is_indexed: on }, { params: { region } });
      toast.success(on ? "Board is now indexable by search engines" : "Board set to noindex");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const decide = async (id, approve) => {
    try {
      await api.post(`/admin/brand-lock/${id}/${approve ? "approve" : "reject"}`);
      toast.success(approve ? "Brand Lock approved" : "Application rejected");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const setRole = async (id, role) => {
    try {
      await api.post(`/admin/users/${id}/role`, { role });
      toast.success("Role updated");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const runBilling = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/billing/run?force_weekly=true");
      toast.success(`Billing run complete — ${data.charges.length} charge(s)`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setRunning(false); }
  };

  if (loading) {
    return <div><div className="flex justify-center pt-40"><Loader2 className="w-8 h-8 animate-spin text-white/40" /></div></div>;
  }
  if (!isAdmin) {
    return <AdminLogin deniedUser={user || null} />;
  }

  const pendingApps = apps.filter((a) => a.status === "pending");

  return (
    <div>
      <Navbar />
      <div className="max-w-5xl mx-auto px-4 pt-28 pb-24">
        <div className="flex items-center justify-between mb-8">
          <motion.h1 initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="font-display font-black text-3xl sm:text-4xl tracking-tighter flex items-center gap-3">
            <Shield className="w-8 h-8 text-blue-400" /> Admin portal
          </motion.h1>
          <Button data-testid="run-billing-btn" onClick={runBilling} disabled={running} className="rounded-full bg-gradient-to-r from-blue-600 to-indigo-600 font-semibold active:scale-95 transition-all">
            {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Play className="w-4 h-4 mr-2" /> Run billing cycle</>}
          </Button>
        </div>

        <Tabs defaultValue="keywords">
          <TabsList className="glass border-white/10 mb-6">
            <TabsTrigger value="keywords" data-testid="admin-tab-keywords"><KeyRound className="w-4 h-4 mr-1.5" /> Keywords</TabsTrigger>
            <TabsTrigger value="brandlock" data-testid="admin-tab-brandlock"><Lock className="w-4 h-4 mr-1.5" /> Brand Lock {pendingApps.length > 0 && <span className="ml-1.5 bg-yellow-500 text-black text-[10px] font-bold rounded-full px-1.5">{pendingApps.length}</span>}</TabsTrigger>
            <TabsTrigger value="users" data-testid="admin-tab-users"><Users className="w-4 h-4 mr-1.5" /> Users</TabsTrigger>
            <TabsTrigger value="messages" data-testid="admin-tab-messages"><Mail className="w-4 h-4 mr-1.5" /> Messages {messages.filter((m) => !m.read).length > 0 && <span className="ml-1.5 bg-blue-500 text-white text-[10px] font-bold rounded-full px-1.5">{messages.filter((m) => !m.read).length}</span>}</TabsTrigger>
          </TabsList>

          <TabsContent value="keywords" className="space-y-3">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-1">
              <div className="glass rounded-full p-1 flex items-center gap-1 shrink-0" data-testid="admin-kw-region-filter">
                {[["all", "All"], ["global", "Global"], ["MY", "MY"]].map(([val, label]) => (
                  <button
                    key={val}
                    data-testid={`admin-kw-region-${val}`}
                    onClick={() => setKwRegionFilter(val)}
                    className={`px-4 h-8 rounded-full text-sm font-medium transition-colors ${kwRegionFilter === val ? "bg-white/15 text-white" : "text-white/50 hover:text-white"}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <div className="glass rounded-full px-4 h-11 flex items-center gap-2 flex-1">
                <Search className="w-4 h-4 text-white/40 shrink-0" />
                <input
                  data-testid="admin-kw-search"
                  value={kwSearch}
                  onChange={(e) => setKwSearch(e.target.value)}
                  placeholder="Search boards by slug or business name…"
                  className="bg-transparent outline-none w-full text-sm placeholder:text-white/30"
                />
                {kwSearch && (
                  <button data-testid="admin-kw-search-clear" onClick={() => setKwSearch("")} className="text-white/40 hover:text-white transition-colors"><X className="w-4 h-4" /></button>
                )}
              </div>
            </div>
            {filteredKeywords.length === 0 && <div className="glass rounded-2xl p-10 text-center text-white/40" data-testid="admin-kw-empty">No keyword boards match your filters.</div>}
            {filteredKeywords.map((k) => {
              const region = k.region || "global";
              const isMY = region === "MY";
              const regionLabel = isMY ? "MY" : "Global";
              return (
              <div key={`${region}-${k.slug}`} data-testid={`admin-kw-${k.slug}`} className="glass rounded-2xl p-5 flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-display font-bold capitalize truncate">{k.name}</span>
                    <span
                      data-testid={`admin-kw-region-badge-${k.slug}`}
                      className={`inline-flex items-center text-[11px] font-bold uppercase tracking-wide rounded-full px-2 py-0.5 border shrink-0 ${isMY ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" : "bg-blue-500/15 text-blue-300 border-blue-500/30"}`}
                    >
                      {regionLabel}
                    </span>
                    {k.verified && <ShieldCheck className="w-4 h-4 text-yellow-400 shrink-0" />}
                    {k.is_indexed
                      ? <span className="inline-flex items-center gap-1 text-xs text-emerald-400 shrink-0"><Globe className="w-3 h-3" /> indexed</span>
                      : <span className="text-xs text-white/30 shrink-0">noindex</span>}
                    {k.brand_lock_user_id && <Lock className="w-3.5 h-3.5 text-yellow-400 shrink-0" />}
                  </div>
                  <div className="text-xs text-white/40">{k.bidder_count} bidders · {k.slug} ({regionLabel})</div>
                </div>
                <div className="flex items-center gap-4 shrink-0">
                  <div className="flex items-center gap-2" title="Toggle search-engine indexing (noindex when off)">
                    <Globe className="w-3.5 h-3.5 text-white/40" />
                    <Switch data-testid={`index-switch-${k.slug}`} checked={!!k.is_indexed} onCheckedChange={(on) => toggleIndex(k.slug, on, region)} />
                  </div>
                  {k.verified ? (
                    <Button data-testid={`unverify-${k.slug}`} variant="outline" size="sm" onClick={() => verify(k.slug, false, region)} className="rounded-full border-white/15 hover:bg-white/10">Unverify</Button>
                  ) : (
                    <Button data-testid={`verify-${k.slug}`} size="sm" onClick={() => verify(k.slug, true, region)} className="rounded-full bg-emerald-500 text-black hover:bg-emerald-400 font-semibold"><Check className="w-4 h-4 mr-1" /> Verify</Button>
                  )}
                </div>
              </div>
              );
            })}
          </TabsContent>

          <TabsContent value="brandlock" className="space-y-3">
            {apps.length === 0 && <div className="glass rounded-2xl p-10 text-center text-white/40">No applications.</div>}
            {apps.map((a) => (
              <div key={a.application_id} data-testid={`admin-bl-${a.application_id}`} className="glass rounded-2xl p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="min-w-0">
                  <div className="font-display font-bold">{a.brand_name}</div>
                  <div className="text-sm text-white/50 capitalize">"{a.keyword}" · {a.website}</div>
                  <div className="text-xs text-white/30">{a.contact_email}</div>
                </div>
                {a.status === "pending" ? (
                  <div className="flex gap-2 shrink-0">
                    <Button data-testid={`approve-${a.application_id}`} size="sm" onClick={() => decide(a.application_id, true)} className="rounded-full bg-emerald-500 text-black hover:bg-emerald-400 font-semibold"><Check className="w-4 h-4 mr-1" /> Approve</Button>
                    <Button data-testid={`reject-${a.application_id}`} variant="outline" size="sm" onClick={() => decide(a.application_id, false)} className="rounded-full border-red-500/30 text-red-400 hover:bg-red-500/10"><X className="w-4 h-4" /></Button>
                  </div>
                ) : (
                  <span className={`text-xs px-2.5 py-1 rounded-full capitalize shrink-0 ${a.status === "approved" ? "bg-emerald-500/15 text-emerald-400" : "bg-red-500/15 text-red-400"}`}>{a.status}</span>
                )}
              </div>
            ))}
          </TabsContent>

          <TabsContent value="users" className="space-y-3">
            <div className="glass rounded-2xl px-4 h-12 flex items-center gap-3 focus-within:ring-2 focus-within:ring-blue-500/40 transition-all">
              <Search className="w-4 h-4 text-white/40 shrink-0" />
              <input
                data-testid="admin-user-search"
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
                placeholder="Search users by name, email or brand…"
                className="flex-1 bg-transparent outline-none text-sm placeholder:text-white/30"
              />
              {userSearch && (
                <button data-testid="admin-user-search-clear" onClick={() => setUserSearch("")} className="text-white/40 hover:text-white transition-colors"><X className="w-4 h-4" /></button>
              )}
            </div>
            {users.length === 0 && <div className="glass rounded-2xl p-10 text-center text-white/40">No users match your search.</div>}
            {users.map((u) => (
              <div key={u.user_id} data-testid={`admin-user-${u.user_id}`} className="glass rounded-2xl p-5 flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <div className="font-medium truncate">{u.name} <span className="text-white/40 text-sm">{u.email}</span></div>
                  <div className="text-xs text-white/40">{u.active_bids} bids · tab ${(u.tab_amount || 0).toFixed(2)}</div>
                </div>
                <Button data-testid={`role-${u.user_id}`} variant="outline" size="sm" onClick={() => setRole(u.user_id, u.role === "admin" ? "user" : "admin")} className="rounded-full border-white/15 hover:bg-white/10 shrink-0 capitalize">
                  {u.role === "admin" ? "Revoke admin" : "Make admin"}
                </Button>
              </div>
            ))}
          </TabsContent>

          <TabsContent value="messages" className="space-y-3">
            {messages.length === 0 && <div className="glass rounded-2xl p-10 text-center text-white/40">No messages yet.</div>}
            {messages.map((m) => (
              <div key={m.message_id} data-testid={`admin-msg-${m.message_id}`} className="glass rounded-2xl p-5">
                <div className="flex items-center justify-between gap-3 mb-1">
                  <div className="font-semibold">{m.name} <a href={`mailto:${m.email}`} className="text-white/40 text-sm font-normal hover:text-white/70">&lt;{m.email}&gt;</a></div>
                  <span className="text-xs text-white/30">{new Date(m.created_at).toLocaleString()}</span>
                </div>
                <p className="text-white/60 text-sm whitespace-pre-wrap">{m.message}</p>
              </div>
            ))}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
